import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../scripts/api';

const SEVERITY_COLORS = { minor: '#d97706', major: '#ef4444', safety: '#7c3aed' };

export default function PdiSelectPage() {
  const nav = useNavigate();
  const [rows,            setRows]            = useState([]);
  const [msg,             setMsg]             = useState('');
  const [busy,            setBusy]            = useState(false);
  const [templates,       setTemplates]       = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [search,          setSearch]          = useState('');
  const [defReport,       setDefReport]       = useState(null);
  const [defBusy,         setDefBusy]         = useState(false);
  const [pdiProgress,     setPdiProgress]     = useState({});
  const [deleteTarget,    setDeleteTarget]    = useState(null); // { sessionId, label }
  const [deletePassword,  setDeletePassword]  = useState('');
  const [deleteBusy,      setDeleteBusy]      = useState(false);
  const [deleteError,     setDeleteError]     = useState('');
  const deletePasswordRef = useRef(null);

  useEffect(() => {
    const activeDb = sessionStorage.getItem('active_db') || '';
    if (!activeDb) { setMsg('Please select a database first.'); return; }
    const load = async () => {
      setBusy(true); setMsg('');
      try {
        const [invRes, tplRes] = await Promise.all([
          api.get('/reports/ops', { params: { table: 'all_inventory', preset: 'in_stock_calgary', limit: 500 } }),
          api.get('/pdi/templates'),
        ]);
        const invRows = invRes.data.rows || [];
        setRows(invRows);
        const tplList = tplRes.data.templates || [];
        setTemplates(tplList);
        if (tplList.length > 0) setSelectedTemplate(tplList[0].template);

        // load PDI progress for all units
        if (invRows.length > 0) {
          const ids = invRows.map(r => r.id).join(',');
          api.get(`/pdi/sessions/bulk-progress?ids=${ids}`)
            .then(r => setPdiProgress(r.data.progress || {}))
            .catch(() => {});
        }
      } catch (e) {
        setMsg(`Failed to load: ${e?.response?.data?.error || e.message}`);
      } finally {
        setBusy(false);
      }
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // filter rows by search term
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      [r.rowo, r.stock_po, r.customer, r.unit_status, r.unit_location, r.body_vin, r.chassis_vin, String(r.id)]
        .some(v => v && String(v).toLowerCase().includes(q))
    );
  }, [rows, search]);

  // when starting PDI: use row's pdi_template if set, else fall back to selected
  const startPdi = (row) => {
    const tpl = row.pdi_template || selectedTemplate;
    nav(`/pdi/${row.id}?template=${encodeURIComponent(tpl)}&table=all_inventory`);
  };

  const refreshProgress = (rows) => {
    if (!rows || rows.length === 0) return;
    const ids = rows.map(r => r.id).join(',');
    api.get(`/pdi/sessions/bulk-progress?ids=${ids}`)
      .then(r => setPdiProgress(r.data.progress || {}))
      .catch(() => {});
  };

  const openDeleteModal = (sessionId, label) => {
    setDeleteTarget({ sessionId, label });
    setDeletePassword('');
    setDeleteError('');
    setTimeout(() => deletePasswordRef.current?.focus(), 80);
  };

  const confirmDelete = async () => {
    if (!deleteTarget || !deletePassword) return;
    setDeleteBusy(true);
    setDeleteError('');
    try {
      await api.delete(`/pdi/sessions/${deleteTarget.sessionId}`, { data: { password: deletePassword } });
      setDeleteTarget(null);
      setDeletePassword('');
      // clear from progress map
      setPdiProgress(prev => {
        const next = { ...prev };
        Object.keys(next).forEach(k => { if (next[k]?.session_id === deleteTarget.sessionId) delete next[k]; });
        return next;
      });
      refreshProgress(rows);
    } catch (e) {
      setDeleteError(e?.response?.data?.error || 'Delete failed');
    } finally {
      setDeleteBusy(false);
    }
  };

  const viewDeficiencies = async (row) => {
    setDefBusy(true); setDefReport(null);
    try {
      const { data } = await api.get(`/pdi/report/inventory/${row.id}/deficiencies`);
      setDefReport({ inventoryId: row.id, rowo: row.rowo, deficiencies: data.deficiencies || [] });
    } catch (e) {
      setMsg(`Failed to load deficiency report: ${e?.response?.data?.error || e.message}`);
    } finally {
      setDefBusy(false);
    }
  };

  return (
    <div className="container">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ margin: 0 }}>PDI — In Stock</h2>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn-outline" onClick={() => nav('/pdi/templates')}>Manage Templates</button>
          <button className="btn-outline" onClick={() => nav('/vin')}>Scan VIN</button>
        </div>
      </div>

      {/* Template selector */}
      <div className="card" style={{ marginTop: 12 }}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>
          Default PDI Template
          <span className="muted" style={{ fontWeight: 400, fontSize: 12, marginLeft: 8 }}>
            (used when a unit has no template assigned)
          </span>
        </div>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {templates.map(t => (
            <button
              key={t.template}
              className={selectedTemplate === t.template ? 'btn' : 'btn-outline'}
              onClick={() => setSelectedTemplate(t.template)}
            >
              {t.title}
              {t.source === 'db' && <span className="muted" style={{ fontSize: 10, marginLeft: 4 }}>custom</span>}
            </button>
          ))}
          {templates.length === 0 && !busy && (
            <div className="muted">
              No templates found. <button className="btn-outline" style={{ marginLeft: 8 }} onClick={() => nav('/pdi/templates')}>Create one</button>
            </div>
          )}
        </div>
      </div>

      {/* Search */}
      <div style={{ marginTop: 12 }}>
        <input
          className="input"
          style={{ width: '100%', fontSize: 15 }}
          placeholder="Search by RO/WO, stock, customer, VIN, status, location…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {busy && <div className="muted" style={{ marginTop: 10 }}>Loading…</div>}
      {msg && <div className="muted" style={{ marginTop: 10, color: '#ef4444' }}>{msg}</div>}

      {/* Deficiency report panel */}
      {defReport && (
        <div className="card" style={{ marginTop: 12, border: '2px solid #ef4444' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontWeight: 700 }}>
              Deficiency Report — Unit #{defReport.inventoryId}
              {defReport.rowo ? ` (RO/WO: ${defReport.rowo})` : ''}
            </div>
            <button className="btn-outline" onClick={() => setDefReport(null)}>Close</button>
          </div>
          {defReport.deficiencies.length === 0 && <div className="muted">No deficiencies on record.</div>}
          {defReport.deficiencies.map(d => (
            <div key={d.id} style={{ borderBottom: '1px solid #eee', padding: '8px 0' }}>
              <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: SEVERITY_COLORS[d.severity] || '#6b7280', textTransform: 'uppercase' }}>{d.severity}</span>
                {d.step_id && <span className="muted" style={{ fontSize: 11 }}>Step {d.step_id}</span>}
                <span style={{ fontWeight: d.status === 'open' ? 600 : 400 }}>{d.description}</span>
                <span style={{ marginLeft: 'auto', fontSize: 11, padding: '2px 8px', borderRadius: 99, background: d.status === 'resolved' ? '#d1fae5' : '#fee2e2', color: d.status === 'resolved' ? '#065f46' : '#991b1b' }}>
                  {d.status}
                </span>
              </div>
              {d.resolution_note && <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>Resolution: {d.resolution_note}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Unit list */}
      {(() => {
        const inProgress = filtered.filter(r => {
          const p = pdiProgress[r.id];
          return p && p.pdi_status === 'in_progress';
        });
        const others = filtered.filter(r => {
          const p = pdiProgress[r.id];
          return !p || p.pdi_status !== 'in_progress';
        });

        const UnitRow = (r) => {
          const p = pdiProgress[r.id];
          const pct = p && p.answered != null ? Math.round((p.answered / (p.total || 1)) * 100) : null;
          return (
            <div key={r.id} style={{ borderBottom: '1px solid #eee', padding: '12px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontWeight: 700 }}>
                    {r.rowo ? `RO/WO ${r.rowo}` : `Unit #${r.id}`}
                    {r.pdi_template && (
                      <span style={{ marginLeft: 8, fontSize: 11, background: '#eff6ff', color: '#1d4ed8', borderRadius: 4, padding: '2px 6px', fontWeight: 600 }}>
                        {r.pdi_template}
                      </span>
                    )}
                    {p?.pdi_status === 'completed' && (
                      <span style={{ marginLeft: 8, fontSize: 11, background: '#dcfce7', color: '#15803d', borderRadius: 4, padding: '2px 6px', fontWeight: 700 }}>
                        ✓ Complete
                      </span>
                    )}
                  </div>
                  <div className="muted" style={{ fontSize: 13 }}>
                    Stock: {r.stock_po || '—'} · {r.customer || '—'}
                  </div>
                  <div className="muted" style={{ fontSize: 13 }}>
                    {r.unit_status || '—'} · {r.unit_location || '—'}
                    {r.body_vin && ` · VIN: ${r.body_vin}`}
                  </div>

                  {/* PDI progress bar */}
                  {p && pct !== null && p.pdi_status !== 'completed' && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#6b7280', marginBottom: 3 }}>
                        <span>{p.answered} / {p.total || '?'} steps</span>
                        <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          {p.open_defs > 0 && (
                            <span style={{ background: '#dc2626', color: '#fff', borderRadius: 99, padding: '1px 6px', fontWeight: 700 }}>
                              {p.open_defs} open
                            </span>
                          )}
                          {pct}%
                        </span>
                      </div>
                      <div style={{ height: 6, background: '#e2e8f0', borderRadius: 99 }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: '#3b82f6', borderRadius: 99, transition: 'width 0.3s' }} />
                      </div>
                    </div>
                  )}
                </div>
                <div className="row" style={{ gap: 6, flexWrap: 'wrap', flexShrink: 0 }}>
                  <button className="btn" onClick={() => startPdi(r)}>
                    {p && p.pdi_status === 'in_progress' ? 'Resume PDI' : 'Start PDI'}
                  </button>
                  <button className="btn-outline" onClick={() => viewDeficiencies(r)} disabled={defBusy}>Deficiencies</button>
                  <button className="btn-outline" onClick={() => nav(`/detail/all_inventory/${r.id}`)}>Record</button>
                  {p?.session_id && (
                    <button
                      className="btn-outline"
                      style={{ color: '#dc2626', borderColor: '#dc2626' }}
                      onClick={() => openDeleteModal(p.session_id, r.rowo ? `RO/WO ${r.rowo}` : `Unit #${r.id}`)}
                    >
                      Cancel PDI
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        };

        return (
          <>
            {inProgress.length > 0 && (
              <div className="card" style={{ marginTop: 12, border: '2px solid #3b82f6' }}>
                <div style={{ fontWeight: 800, fontSize: 14, color: '#1d4ed8', marginBottom: 4 }}>
                  In Progress ({inProgress.length})
                </div>
                {inProgress.map(UnitRow)}
              </div>
            )}
            <div className="card" style={{ marginTop: 12 }}>
              <div style={{ marginBottom: 8, fontWeight: 700, fontSize: 13, color: '#6b7280' }}>
                {others.length} unit{others.length !== 1 ? 's' : ''}
                {search && ` matching "${search}"`}
              </div>
              {others.length === 0 && !busy && <div className="muted">No units found.</div>}
              {others.map(UnitRow)}
            </div>
          </>
        );
      })()}

      {/* ── Delete / Cancel PDI modal ── */}
      {deleteTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div className="card" style={{ maxWidth: 400, width: '100%' }}>
            <div style={{ fontWeight: 900, fontSize: 18, marginBottom: 4, color: '#dc2626' }}>Cancel / Delete PDI</div>
            <div className="muted" style={{ marginBottom: 16, fontSize: 14 }}>
              This will permanently delete the PDI session, all step responses, and all deficiencies for <strong>{deleteTarget.label}</strong>. This cannot be undone.
            </div>
            <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 14 }}>Enter your password to confirm</div>
            <input
              ref={deletePasswordRef}
              className="input"
              type="password"
              placeholder="Your password…"
              value={deletePassword}
              onChange={e => setDeletePassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && confirmDelete()}
              style={{ width: '100%', marginBottom: 10, boxSizing: 'border-box' }}
            />
            {deleteError && (
              <div style={{ color: '#dc2626', fontWeight: 700, fontSize: 14, marginBottom: 10 }}>{deleteError}</div>
            )}
            <div className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn-outline" onClick={() => setDeleteTarget(null)} disabled={deleteBusy}>Cancel</button>
              <button
                className="btn"
                style={{ background: '#dc2626' }}
                onClick={confirmDelete}
                disabled={deleteBusy || !deletePassword}
              >
                {deleteBusy ? 'Deleting…' : 'Delete PDI'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
